import { ref, computed } from 'vue'
import { adminService } from '@/services/adminService'
import { useToast } from '@/composables/useToast'

/**
 * Composable for additional document request logic (admin modals)
 * Extracts shared state, computed, methods and formatters used by
 * CaseDetailsModal and DocumentationValidationModal
 *
 * @param {import('vue').Ref<string>|import('vue').ComputedRef<string>} caseIdRef - Reactive case ID
 * @param {Object} options
 * @param {Function} [options.onSuccess] - Callback after successful request
 */
export function useAdditionalDocuments(caseIdRef, { onSuccess } = {}) {
  const toast = useToast()

  // State
  const newDocName = ref('')
  const newDocReason = ref('')
  const requestingDoc = ref(false)

  // Computed
  const canRequestDoc = computed(
    () => newDocName.value.trim().length >= 3 && newDocReason.value.trim().length >= 10
  )

  // Formatters
  const getAdditionalDocStatusClass = (status) => {
    const classes = {
      pending: 'bg-amber-100 text-amber-800',
      uploaded: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    }
    return `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${classes[status] || classes.pending}`
  }

  const formatAdditionalDocStatus = (status) => {
    const labels = { pending: 'Pendiente', uploaded: 'Subido', approved: 'Aprobado', rejected: 'Rechazado' }
    return labels[status] || 'Pendiente'
  }

  // Actions
  const requestAdditionalDoc = async () => {
    if (!canRequestDoc.value || requestingDoc.value) return

    const caseId = typeof caseIdRef === 'function' ? caseIdRef() : caseIdRef.value
    if (!caseId) return

    requestingDoc.value = true
    try {
      await adminService.requestAdditionalDocument(caseId, {
        name: newDocName.value.trim(),
        reason: newDocReason.value.trim()
      })
      toast.success('Documento adicional solicitado correctamente')
      newDocName.value = ''
      newDocReason.value = ''
      onSuccess?.()
    } catch (error) {
      toast.error(error.message || 'Error al solicitar documento adicional')
    } finally {
      requestingDoc.value = false
    }
  }

  const resetState = () => {
    newDocName.value = ''
    newDocReason.value = ''
    requestingDoc.value = false
  }

  return {
    newDocName,
    newDocReason,
    requestingDoc,
    canRequestDoc,
    getAdditionalDocStatusClass,
    formatAdditionalDocStatus,
    requestAdditionalDoc,
    resetAdditionalDocsState: resetState
  }
}
